(ns client-portal.client.voice-insights.data
  (:require [cljs-time.core :as t]
            [cljs-time.format :as f]
            [cljs-time.coerce :as coerce]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.day-part-schedules.data :as day-part-schedules.d]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.time :as time]
            [shared.core.util :as util]
            [shared.core.style :as style]
            ["bad-words" :as bad-words]))

(def root-path [:voice-insights])
(def data-path (conj root-path :data))
(def manage-path (conj root-path :manage))
(def selected-day-part-path (conj manage-path :selected-day-part))
(def tag-bar-open-count-path (conj manage-path :tag-bar-open-count))
(def configuration-path (conj root-path :configuration))
(def concept-configuration-path (conj root-path :concept-configuration))
(def date-formatter (f/formatter "yyyy-MM-dd"))

(def conversations-count-path [:conversations-count])
(def conversations-duration-path [:conversations-duration])
(def conversations-duration-over-time-path [:conversations-duration-over-time])
(def scores-by-category [:scores :by-category])
(def concept-scores-by-category [:concept-scores :by-category])
(def ces-score [:ces-score])
(def concept-ces-score [:concept-ces-score])
(def ces-score-over-time [:ces-score-over-time])
(def scores-by-category-over-time [:scores :by-category-over-time])
(def scores-by-category-by-location [:scores :by-category-by-location])
(def conversation-tags-count-path [:conversation-tags-count])
(def crew-conversations-utterance-count-path [:crew-conversations-utterance-count])
(def user-conversations-utterance-count-path [:user-conversations-utterance-count])
(def crew-conversations-utterance-count-over-time-path [:crew-conversations-utterance-count-over-time])
(def user-conversations-utterance-count-over-time-path [:user-conversations-utterance-count-over-time])

(def day-part-all-day {:label "all day"})
(def namespace-default "main")

(def category-efficiency "efficiency")
(def category-marketing "marketing")
(def category-satisfaction "satisfaction")
(def category-other "other")

(def category-info
  {category-marketing {:colors {:base (style/alc-color "Chart/tech_blue")
                                :gradient (style/alc-color "Chart/tech_blue")}
                       :sort-order 1}
   category-efficiency {:colors {:base (style/alc-color "Chart/orchid")
                                 :gradient (style/alc-color "Chart/orchid")}
                        :sort-order 2}
   category-satisfaction {:colors {:base (style/alc-color "Chart/turquoise")
                                   :gradient (style/alc-color "Chart/turquoise")
                                   :text-color (style/alc-color "Text/primary_const")
                                   :pct-color (style/alc-color "Text/primary_const")}
                          :sort-order 3}
   category-other {:colors {:base "#636566"
                            :gradient "#636566"}
                   :sort-order 4}})

(def tag-info
  {:combo-upsell {:sort-order 1
                  :value-orders {:offered 1 :not-offered 2}}
   :size-upsell {:sort-order 2
                 :value-orders {:offered 1 :not-offered 2}}
   :add-on-upsell {:sort-order 3
                   :value-orders {:offered 1 :not-offered 2}}
   :start {:sort-order 4
           :value-orders {:on-time 1 :delayed 2}}
   :pace {:sort-order 5
          :value-orders {:normal 1 :slow 2 :fast 3}}
   :greeting {:sort-order 6
              :value-orders {:neutral 1 :positive 2 :negative 3}}
   :sentiment {:sort-order 7
               :value-orders {:neutral 1 :positive 2 :negative 3}}
   :outcome {:sort-order 8
             :value-orders {:fulfilled 1 :online-pickup 2 :delivery-pickup 3 :driveoff 4 :undetermined 5}}
   :ai-outcome {:sort-order 9
                :value-orders {:ai-fulfilled 1 :takeover 2 :customer-escalation 3 :ai-escalation 4 :undetermined 5}}})

(def speaker-info
  {:crew {:display-name "Crew"}
   :crew-ai {:display-name "Crew [AI]"}
   :crew-human {:display-name "Crew [Human]"}
   :agent {:display-name "Agent"}
   :agent-ai {:display-name "Agent [AI]"}
   :agent-human {:display-name "Agent [Human]"}
   :user {:display-name "Customer"}})

(defn ->speaker-display-name [speaker] (get-in speaker-info [(keyword speaker) :display-name]))

(defn category-color [category color] (get-in category-info [category :colors color]))

(defn clean-bad-words [text]
  (try
    (when (not-empty text)
      (let [filter (bad-words.)]
        (.clean filter text)))
    (catch :default _
      text)))

(defn tag-sort-order
  ([tag] (get-in tag-info [tag :sort-order] 99))
  ([tag value] (get-in tag-info [tag :value-orders (keyword value)] 99)))

(defn opacity-gradient
  "Returns a vector of `n` opacities, linearly spaced from `max-opacity` to `min-opacity`"
  [n & {:keys [min-opacity max-opacity]
        :or {min-opacity 0.3
             max-opacity 1.0}}]
  (if (> n 1)
    (->> (range n)
         (mapv #(+ min-opacity (* (- max-opacity min-opacity)
                                  (- 1 (/ % (dec n)))))))
    [max-opacity]))

(defn selected-day-part [db] (get-in db selected-day-part-path))

(defn days-ago->date
  [days-ago]
  (let [target-date (t/minus (t/time-now) (t/days days-ago))]
    (f/unparse date-formatter target-date)))

(defn core-data-path [db args]
  (conj data-path
        (concepts.d/current-concept-id db)
        (locations.d/current-selected-location-id db)))

(defn voice-insights-path
  [db path & args]
  (into (core-data-path db args) path))

(defn matches-category
  [category coll]
  (filter #(= (:category %) category) coll))

(defn sort-by-date
  [k coll]
  (sort-by #(f/parse date-formatter (get % k)) t/before? coll))

(re/reg-event-db
  :voice-insights/initialize-filters re/middleware
  (fn [db]
    (let [{:keys [dp]} (router/current-query-params db)]
      (-> (core.d/initialize-common-filters db)
          (assoc-in tag-bar-open-count-path nil)
          (assoc-in selected-day-part-path (or (when (not-empty dp) dp)
                                               (:label day-part-all-day)))))))

(re/reg-event-fx
  :voice-insights/initialize re/middleware
  (fn [_]
    (re/dispatch [:voice-insights/initialize-filters])
    (re/dispatch [:voice-insights/reload-analytics])
    {:forward-events {:register :vi-initialization-listener
                      :events #{:concepts/select :locations/select :core/select-crew-type
                                :core/select-timeframe :voice-insights/select-day-part
                                :core/timeframe-date-range-changed}
                      :dispatch-to [:voice-insights/reload-analytics]}}))

(defn filter-data-from-route [db]
  (some->> (router/current-query-params db)
           :fd
           (util/base-64-decode)
           (util/json-string->map)
           (mapv #(if (some-> (:value %) (vector?))
                    (-> %
                        (update :value (fn [vals]
                                         (mapv (fn [v]
                                                 (if (map? v)
                                                   (util/->kebab-case-string v)
                                                   v))
                                               vals)))
                        (update :children (fn [children]
                                            (mapv (fn [child]
                                                    (update child :value (fn [vals]
                                                                           (mapv (fn [v]
                                                                                   (if (map? v)
                                                                                     (util/->kebab-case-string v)
                                                                                     v))
                                                                                 vals))))
                                                  children))))
                    %))))

(re/reg-sub :voice-insights/filter-data- filter-data-from-route)

(defn build-date-filters [db {:keys [min-timeframe timeframe start-date end-date]}]
  (let [qps (router/current-query-params db)
        selected-date (:date qps)
        timeframe (or timeframe (core.d/selected-timeframe db))
        yesterday? (= timeframe 1)
        most-recent? (= timeframe core.d/most-recent-timeframe-value)]
    {:start-date (or start-date
                     (:start qps)
                     (if selected-date
                       (->> (t/minus (f/parse date-formatter selected-date) (t/days 7))
                            (f/unparse date-formatter))
                       (when-not most-recent?
                         (days-ago->date (max min-timeframe timeframe)))))
     :end-date (or end-date
                   ;; If we have an end date qp, let's add a day on so it's inclusive of that day
                   (some-> (:end qps)
                           (coerce/from-string)
                           (t/plus (t/days 1))
                           (#(f/unparse date-formatter %)))
                   selected-date
                   (when-not most-recent?
                     (days-ago->date (if yesterday? 0 -1))))}))

(defn base-query-args
  [db {:keys [concept-id location-id limit use-day-part?] :as args}]
  (let [concept-id (or concept-id (when (not (false? concept-id)) (concepts.d/current-concept-id db)))
        location-id (or location-id (when (not (false? location-id)) (locations.d/current-selected-location-id db)))
        qps (router/current-query-params db)
        timeframe (get-in db core.d/timeframe-path)
        live-data? (= timeframe -2)
        {:keys [start-date end-date]} (build-date-filters db args)
        day-part (when use-day-part? (selected-day-part db))
        {:keys [start-time end-time]} (when day-part
                                        (or (when location-id
                                              (some->> (util/when-not-loading (day-part-schedules.d/current-day-parts db))
                                                       (filter #(and (= (:label %) day-part)
                                                                     (= (:concept-id %) concept-id)
                                                                     (= (:location-id %) location-id)))
                                                       (first)))
                                            (some->> (util/when-not-loading (day-part-schedules.d/current-day-parts db))
                                                     (filter #(and (= (:label %) day-part)
                                                                   (= (:concept-id %) concept-id)))
                                                     (first))))
        selected-crew-type (core.d/selected-crew-type db)
        filters (filter-data-from-route db)]
    (cond-> {:concept-id concept-id
             :location-id location-id
             :start-date start-date
             :end-date end-date
             :namespace (or (:ns qps) namespace-default)
             :timeframe timeframe}

            (and start-time end-time)
            (assoc :day-part-start-time start-time
                   :day-part-end-time end-time)

            (:dsid qps)
            (assoc :dataset-id (:dsid qps))

            (or live-data? (:ds qps))
            (assoc :dataset (if live-data? "voice_insights_live" (:ds qps)))

            selected-crew-type
            (assoc :crew-type selected-crew-type)

            limit
            (assoc :limit limit)

            (not-empty filters)
            (assoc :filters filters))))

(re/reg-auth0-loader
  :voice-insights/conversations-count
  (fn [_ db]
    (voice-insights-path db conversations-count-path))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/conversations/count"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords) :count)
                         ""))
  :error-transf (fn [] ""))

(re/reg-auth0-load-sub
  :voice-insights/conversations-count-
  (fn [_ db]
    (voice-insights-path db conversations-count-path))
  :voice-insights/conversations-count)

(re/reg-auth0-loader
  :voice-insights/conversations-avg-duration
  (fn [_ db]
    (voice-insights-path db conversations-duration-path))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/conversations/duration"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords) :duration (js/Math.round) (time/seconds->format))
                         ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/conversations-avg-duration-
  (fn [_ db]
    (voice-insights-path db conversations-duration-path))
  :voice-insights/conversations-avg-duration)

(re/reg-auth0-loader
  :voice-insights/conversations-avg-duration-over-time
  (fn [_ db]
    (voice-insights-path db conversations-duration-over-time-path))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/conversations/duration/over-time"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/conversations-avg-duration-over-time-
  (fn [_ db]
    (voice-insights-path db conversations-duration-over-time-path))
  :voice-insights/conversations-avg-duration-over-time)

(defn load-conversations-utterance-count [db {:keys [speaker] :as args}]
  (when speaker
    {:method :post
     :url "/api/vi/utterances/by-speaker/average"
     :json-params (assoc (base-query-args db (assoc args :use-day-part? true))
                    :speaker speaker)}))

(defn transf-conversations-utterance-count [resp]
  (or (some-> resp :body (util/->kebab-case-keywords) :count (util/to-precision))
      ""))

(re/reg-auth0-loader
  :voice-insights/crew-conversations-utterance-count
  (fn [_ db]
    (voice-insights-path db crew-conversations-utterance-count-path))
  (fn [db]
    (load-conversations-utterance-count db {:speaker "crew"}))
  :transf transf-conversations-utterance-count
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/crew-conversations-utterance-count-
  (fn [_ db]
    (voice-insights-path db crew-conversations-utterance-count-path))
  :voice-insights/crew-conversations-utterance-count)

(re/reg-auth0-loader
  :voice-insights/user-conversations-utterance-count
  (fn [_ db]
    (voice-insights-path db user-conversations-utterance-count-path))
  (fn [db]
    (load-conversations-utterance-count db {:speaker "user"}))
  :transf transf-conversations-utterance-count
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/user-conversations-utterance-count-
  (fn [_ db]
    (voice-insights-path db user-conversations-utterance-count-path))
  :voice-insights/user-conversations-utterance-count)

(defn load-conversations-utterance-count-over-time [db {:keys [speaker] :as args}]
  (when speaker
    {:method :post
     :url "/api/vi/utterances/by-speaker/average/over-time"
     :json-params (assoc (base-query-args db (assoc args :min-timeframe 7
                                                         :use-day-part? true))
                    :speaker speaker)}))

(defn transf-conversations-utterance-count-over-time [resp]
  (or (some-> resp :body (util/->kebab-case-keywords))
      {}))

(re/reg-auth0-loader
  :voice-insights/crew-conversations-utterance-count-over-time
  (fn [_ db]
    (voice-insights-path db crew-conversations-utterance-count-over-time-path))
  (fn [db]
    (load-conversations-utterance-count-over-time db {:speaker "crew"}))
  :transf transf-conversations-utterance-count-over-time
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/crew-conversations-utterance-count-over-time-
  (fn [_ db]
    (voice-insights-path db crew-conversations-utterance-count-over-time-path))
  :voice-insights/crew-conversations-utterance-count-over-time)

(re/reg-auth0-loader
  :voice-insights/user-conversations-utterance-count-over-time
  (fn [_ db]
    (voice-insights-path db user-conversations-utterance-count-over-time-path))
  (fn [db]
    (load-conversations-utterance-count-over-time db {:speaker "user"}))
  :transf transf-conversations-utterance-count-over-time
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/user-conversations-utterance-count-over-time-
  (fn [_ db]
    (voice-insights-path db user-conversations-utterance-count-over-time-path))
  :voice-insights/user-conversations-utterance-count-over-time)

(defn transf-scores-by-category [resp]
  (or (some-> resp :body (util/->kebab-case-keywords))
      ""))

(re/reg-auth0-loader
  :voice-insights/scores-by-category
  (fn [_ db]
    (voice-insights-path db scores-by-category))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/scores/by-category"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf transf-scores-by-category
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/scores-by-category-
  (fn [_ db]
    (voice-insights-path db scores-by-category))
  :voice-insights/scores-by-category)

(re/reg-auth0-loader
  :voice-insights/concept-scores-by-category
  (fn [_ db]
    (voice-insights-path db concept-scores-by-category))
  (fn [db]
    {:method :post
     :url "/api/vi/scores/by-category"
     :json-params (base-query-args db {:location-id false})})
  :transf transf-scores-by-category
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/concept-scores-by-category-
  (fn [_ db]
    (voice-insights-path db concept-scores-by-category :location-id false))
  :voice-insights/concept-scores-by-category)

;; CES SCORES

(re/reg-auth0-loader
  :voice-insights/ces-score
  (fn [_ db]
    (voice-insights-path db ces-score))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/scores/ces"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf #(or (some-> % :body (first) :score)
               0)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :voice-insights/ces-score-
  (fn [_ db]
    (voice-insights-path db ces-score))
  :voice-insights/ces-score)

(re/reg-auth0-loader
  :voice-insights/concept-ces-score
  (fn [_ db]
    (voice-insights-path db concept-ces-score))
  (fn [db]
    {:method :post
     :url "/api/vi/scores/ces"
     :json-params (base-query-args db {:location-id false})})
  :transf #(or (some-> % :body (first) :score)
               0)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :voice-insights/concept-ces-score-
  (fn [_ db]
    (voice-insights-path db concept-ces-score :location-id false))
  :voice-insights/concept-ces-score)

(re/reg-auth0-loader
  :voice-insights/ces-score-over-time
  (fn [_ db]
    (voice-insights-path db ces-score-over-time))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/scores/ces/over-time"
     :json-params (base-query-args db (assoc args :min-timeframe 7
                                                  :use-day-part? true))})
  :transf (fn [resp] (or (:body resp) ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/ces-score-over-time-
  (fn [_ db]
    (voice-insights-path db ces-score-over-time))
  :voice-insights/ces-score-over-time)

;; END CES SCORES

(re/reg-auth0-loader
  :voice-insights/scores-by-category-over-time
  (fn [_ db]
    (voice-insights-path db scores-by-category-over-time))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/scores/by-category/over-time"
     :json-params (base-query-args db (assoc args :min-timeframe 7
                                                  :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/scores-by-category-over-time-
  (fn [_ db]
    (voice-insights-path db scores-by-category-over-time))
  :voice-insights/scores-by-category-over-time)

(re/reg-auth0-loader
  :voice-insights/scores-by-category-by-location
  (fn [_ db]
    (voice-insights-path db scores-by-category-by-location))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/scores/by-category/by-location"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/scores-by-category-by-location-
  (fn [_ db]
    (voice-insights-path db scores-by-category-by-location))
  :voice-insights/scores-by-category-by-location)

(re/reg-auth0-loader
  :voice-insights/conversation-tags-count
  (fn [_ db]
    (voice-insights-path db conversation-tags-count-path))
  (fn [db [_ args]]
    {:method :post
     :url "/api/vi/conversations/tags/count"
     :json-params (base-query-args db (assoc args :use-day-part? true))})
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         ""))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :voice-insights/conversation-tags-count-
  (fn [_ db]
    (voice-insights-path db conversation-tags-count-path))
  :voice-insights/conversation-tags-count)

(defn tag-bar-open-count [db tag]
  (-> (get-in db tag-bar-open-count-path {})
      (get tag)
      (or 0)))

(re/reg-sub
  :voice-insights/tag-bar-open-count-
  (fn [db [_ tag]]
    (tag-bar-open-count db tag)))

(re/reg-event-db
  :voice-insights/toggle-tag-bar-open re/middleware
  (fn [db [tag]]
    (let [open? (> (tag-bar-open-count db tag) 0)]
      (assoc-in db tag-bar-open-count-path
                (assoc (get-in db tag-bar-open-count-path {}) tag (if open? 0 5))))))

(re/reg-event-db
  :voice-insights/increase-tag-bar-open-count re/middleware
  (fn [db [tag]]
    (let [count (tag-bar-open-count db tag)]
      (assoc-in db tag-bar-open-count-path
                (assoc (get-in db tag-bar-open-count-path {}) tag (+ count 5))))))

(re/reg-sub
  :voice-insights/timeframe-
  (fn [db _]
    (get-in db core.d/timeframe-path)))

(re/reg-sub
  :voice-insights/selected-day-part-
  selected-day-part)

(re/reg-event-db
  :voice-insights/select-day-part re/middleware
  (fn [db [day-part]]
    (assoc-in db selected-day-part-path day-part)))

(re/reg-sub
  :voice-insights/crew-name-
  :<- [:core/selected-crew-type-]
  :<- [:voice-insights/ces-feature-enabled?-]
  (fn [[ct ces?]]
    (if ces?
      "Agent"
      "Crew")))

(re/reg-sub
  :voice-insights/satisfaction-name-
  :<- [:vi-tags/list-by-current-concept-id-]
  (fn [tags]
    (if true                                                ; check for `user-sentiment` tag
      "Sentiment"
      "Satisfaction")))

(re/reg-event-db
  :voice-insights/reload-analytics re/middleware
  (fn [db]
    (assoc-in db (core-data-path db nil) nil)))

(re/reg-event-db
  :voice-insights/clear-concepts-data re/middleware
  (fn [db]
    (assoc-in db (conj root-path :concepts) nil)))

(re/reg-event-db
  :voice-insights/scroll-to-top re/middleware
  (fn [db]
    (when-let [el (js/document.getElementById "core-scroll-content")]
      (set! (.-scrollTop el) 0))
    db))

(defn configuration [db]
  (get-in db (conj configuration-path [(concepts.d/current-concept-id db)
                                       (locations.d/current-selected-location-id db)])))

(re/reg-auth0-loader
  :voice-insights/load-configuration
  (fn [_ db]
    (conj configuration-path [(concepts.d/current-concept-id db) (locations.d/current-selected-location-id db)]))
  (fn [db]
    (when-let [concept-id (concepts.d/current-concept-id db)]
      (let [location-id (locations.d/current-selected-location-id db)]
        {:method :post
         :url "/api/vi/configuration"
         :json-params (cond-> {:concept-id concept-id}
                              location-id (assoc :location-id location-id))})))
  :transf (fn [resp] (or (some-> resp
                                 :body
                                 (util/->kebab-case-keywords)
                                 (update :features #(into #{} %)))
                         {}))
  :error-transf (fn [] {})
  :quiet-fail? true)

(re/reg-auth0-load-sub
  :voice-insights/configuration-
  (fn [_ db]
    (conj configuration-path [(concepts.d/current-concept-id db) (locations.d/current-selected-location-id db)]))
  :voice-insights/load-configuration)

(defn concept-configuration [db]
  (get-in db (conj concept-configuration-path (concepts.d/current-concept-id db))))

(re/reg-auth0-loader
  :voice-insights/load-concept-configuration
  (fn [_ db]
    (conj concept-configuration-path (concepts.d/current-concept-id db)))
  (fn [db]
    (when-let [concept-id (concepts.d/current-concept-id db)]
      {:method :post
       :url "/api/vi/configuration"
       :json-params {:concept-id concept-id}}))
  :transf (fn [resp] (or (some-> resp
                                 :body
                                 (util/->kebab-case-keywords)
                                 (update :features #(into #{} %)))
                         {}))
  :error-transf (fn [] {})
  :quiet-fail? true)

(re/reg-auth0-load-sub
  :voice-insights/concept-configuration-
  (fn [_ db]
    (conj concept-configuration-path (concepts.d/current-concept-id db)))
  :voice-insights/load-concept-configuration)

(defn config-has-feature-enabled? [config feature]
  (or (some-> (util/when-not-loading config)
              :features
              (contains? feature))
      false))

(defn ces-feature-enabled? [config]
  (config-has-feature-enabled? config "customer-effort-score"))

(re/reg-sub
  :voice-insights/ces-feature-enabled?-
  :<- [:voice-insights/concept-configuration-]
  (fn [config]
    (ces-feature-enabled? config)))

(defn menu-items-feature-enabled? [config]
  (config-has-feature-enabled? config "menu-items"))

(re/reg-sub
  :voice-insights/menu-items-feature-enabled?-
  :<- [:voice-insights/concept-configuration-]
  (fn [config]
    (menu-items-feature-enabled? config)))

(re/reg-sub
  :voice-insights/upsell-feature-enabled?-
  :<- [:voice-insights/concept-configuration-]
  (fn [config]
    (config-has-feature-enabled? config "upsell")))

(re/reg-sub
  :voice-insights/live-data?-
  :<- [:voice-insights/concept-configuration-]
  (fn [config]
    (:live-data config)))
